import React from "react";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import InfoTile from "../../../../components/common/InfoTile";
import { MAROON, MAROON_TINT } from "../../../../components/common/brandColors";
import { useHasPermission } from "../../../auth/useClaims";
import VehicleRequestAttachment from "./VehicleRequestAttachment";

// Read-only detail block for a MaintenanceRequestDetailRecord (full detail,
// has `.spareItems` - not the lighter list-row shape). Originally built
// inline on LpoDetail.jsx's LinkedDocumentsPanel (an LPO created from a
// Vehicle Maintenance Request), extracted here so the Vehicle Maintenance
// Report/History screen can show the exact same thing for a request that
// has no LPO at all yet, without duplicating the markup.
const VehicleRequestDetailCard = ({ request }) => {
  // Attach / replace / remove: requester or approver, at any status (2026-09-23). The
  // API applies the same rule; anyone who can see the request can view the file.
  const canCreate = useHasPermission("Vehicles.CreateRequest");
  const canApprove = useHasPermission("Vehicles.ApproveRequest");
  if (!request) return <p className="text-muted mb-0">Loading...</p>;

  return (
    <>
      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3"><InfoTile label="Request No" value={request.requestNumber} tone="maroon" /></div>
        <div className="col-6 col-md-3"><InfoTile label="Requested By" value={request.requestedByName} /></div>
        <div className="col-6 col-md-3"><InfoTile label="Mileage" value={request.currentMileage ?? "-"} /></div>
        <div className="col-6 col-md-3">
          <InfoTile label="Status"><StatusBadge status={request.status} /></InfoTile>
        </div>
      </div>
      {request.serviceRepairDescription && (
        <div className="rounded p-2 mb-2" style={{ background: MAROON_TINT, borderLeft: `4px solid ${MAROON}` }}>
          <div className="fw-semibold mb-1" style={{ color: MAROON }}>Service / Repair</div>
          {request.serviceRepairDescription}
        </div>
      )}
      {request.cashRequestRemarks && (
        <div className="rounded p-2 mb-2" style={{ background: MAROON_TINT, borderLeft: `4px solid ${MAROON}` }}>
          <div className="fw-semibold mb-1" style={{ color: MAROON }}>Cash Requested</div>
          {request.cashRequestRemarks}
          {request.approvedCashAmount != null && (
            <div className="mt-1"><strong>Approved Amount:</strong> {Number(request.approvedCashAmount).toFixed(2)}</div>
          )}
        </div>
      )}
      {request.spareSupplierName && (
        <div className="rounded p-2 mb-2" style={{ background: "#F1F2F4", color: "#212529" }}>
          <span className="fw-semibold">Supplier (as requested): </span>{request.spareSupplierName}
        </div>
      )}
      {request.remarks && (
        <div className="rounded p-2 mb-2" style={{ background: "#F1F2F4", color: "#212529" }}>
          <span className="fw-semibold">Remarks: </span>{request.remarks}
        </div>
      )}
      <VehicleRequestAttachment
        recordGuid={request.recordGuid}
        hasAttachment={request.hasAttachment}
        fileName={request.attachmentFileName}
        canManage={canCreate || canApprove}
      />
      {request.approvedByName && (
        <div className="alert alert-success py-2 mb-3">
          Approved by <ActorName action="Approved" name={request.approvedByName} /> on {new Date(request.approvedDate).toLocaleString()}
        </div>
      )}
      {request.rejectedByName && (
        <div className="alert alert-danger py-2 mb-3">
          Rejected by <strong>{request.rejectedByName}</strong>{request.rejectionReason ? `: ${request.rejectionReason}` : ""}
        </div>
      )}
      {request.spareItems?.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <thead>
              <tr style={{ background: MAROON_TINT }}>
                <th>#</th><th>Product</th><th className="text-end">Qty</th><th>Unit</th><th className="text-end">Est. Cost</th>
              </tr>
            </thead>
            <tbody>
              {request.spareItems.map((it, idx) => (
                <tr key={it.itemId}>
                  <td>{idx + 1}</td>
                  <td>{it.productName || it.productCode}</td>
                  <td className="text-end">{it.qty ?? "-"}</td>
                  <td>{it.unit || "-"}</td>
                  <td className="text-end">{it.estimatedCost != null ? Number(it.estimatedCost).toFixed(2) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default VehicleRequestDetailCard;
